import {skipToken} from '@reduxjs/toolkit/query';

import {useEffect, useState} from 'react';
import TagManager from 'react-gtm-module';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useRequiredParams} from 'shared/web';

import {useGetPriceReportQuery, useGetRequestQuery} from '../store/priceReportApi';

const POLLING_INTERVAL = 10000;

export function usePriceReport() {
  const {requestId} = useRequiredParams();
  const [isPending, setIsPending] = useState(false);
  const {
    data: request,
    isLoading: isRequestLoading,
    isFetching: isRequestFetching,
    isError: isRequestError,
    error: requestError,
  } = useGetRequestQuery({id: requestId});
  const {
    data: priceReport,
    isLoading: isReportLoading,
    isError: isReportError,
    error: reportError,
  } = useGetPriceReportQuery(
    request?.idPriceReport ? {reportId: request.idPriceReport} : skipToken,
    {pollingInterval: isPending ? POLLING_INTERVAL : undefined}
  );

  const isLoading = isRequestLoading || isReportLoading;
  const isError = isRequestError || isReportError;
  const isNotFound =
    isNotNil(requestError) && 'status' in requestError && requestError.status === 404;
  const isNotCreated =
    !isRequestFetching && !isRequestError && !isNotFound && isNilOrEmpty(request?.idPriceReport);
  const isExpired = isNotNil(reportError) && 'status' in reportError && reportError.status === 410;

  useEffect(() => {
    setIsPending(priceReport?.status === 'PENDING');
  }, [priceReport?.status]);

  useEffect(() => {
    if (
      isNil(priceReport?.id) ||
      isNilOrEmpty(process.env.GTM_CONTAINER_ID) ||
      process.env.ENV_TYPE !== 'prod'
    ) {
      return;
    }

    const dataLayer = {
      environment: process.env.ENV_TYPE,
      priceReportId: priceReport!.id ?? '',
    };

    TagManager.dataLayer({dataLayer});
  }, [priceReport, priceReport?.id]);

  return {
    isNotCreated,
    isNotFound,
    isExpired,
    isLoading,
    isError,
    priceReport,
    requestData: request,
  };
}
